import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { dashboardAPI, authAPI, bookieAPI } from '../services/api';
import './ClubStatement.css';

function ClubStatement() {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statementData, setStatementData] = useState(null);
  const [activeTab, setActiveTab] = useState('kitty'); // 'kitty' or 'betting'
  const [sortColumn, setSortColumn] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [theme] = useState(localStorage.getItem('theme') || 'light');
  const [bookieBalances, setBookieBalances] = useState(null);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    loadStatementData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId]);

  const loadStatementData = async () => {
    try {
      setLoading(true);
      // First verify user is authenticated
      await authAPI.getCurrentUser();

      // Load dashboard data which contains most of what we need
      const data = await dashboardAPI.getClubDashboard(clubId);
      setStatementData(data);

      // Fetch live balances from bookie
      try {
        const balanceData = await bookieAPI.getBookieBalance(clubId);
        if (balanceData.success) {
          setBookieBalances(balanceData.balances);
        }
      } catch (e) {
        // Non-critical — fall back to local balances
      }
    } catch (err) {
      console.error('Error loading statement data:', err);
      setError(err.response?.data?.error || 'Failed to load club statement');
      if (err.response?.status === 401) {
        setTimeout(() => navigate('/login'), 2000);
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' });
    const suffix = getDaySuffix(day);
    return `${month} ${day}${suffix}`;
  };

  const getDaySuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortedMembers = () => {
    if (!statementData?.members) return [];

    return [...statementData.members].sort((a, b) => {
      let aVal, bVal;

      if (sortColumn === 'name') {
        aVal = a.name?.toLowerCase() || '';
        bVal = b.name?.toLowerCase() || '';
      } else if (sortColumn === 'contributions') {
        aVal = a.contributions || 0;
        bVal = b.contributions || 0;
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  };

  if (loading) {
    return (
      <div className="loading">Loading club statement...</div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Error</h2>
        <p className="error-message">{error}</p>
      </div>
    );
  }

  if (!statementData) {
    return null;
  }

  const { club, members, accounts, puntingSchedule } = statementData;
  const sortedMembers = getSortedMembers();

  return (
    <div>
      <h2>Club Statement</h2>

      <div className="statement-container">
        {/* Club Details Section */}
        <section className="statement-section">
          <h2><i className="fas fa-info-circle"></i> Club Details</h2>
          <div className="details-grid">
            <div className="detail-row">
              <span className="detail-label">Betting Balance</span>
              <span className="detail-value">
                {bookieBalances
                  ? formatCurrency(bookieBalances.betting.cents)
                  : formatCurrency(accounts?.betting?.balance || 0)}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Kitty Balance</span>
              <span className="detail-value">
                {bookieBalances
                  ? formatCurrency(bookieBalances.kitty.cents)
                  : formatCurrency(accounts?.kitty?.balance || 0)}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Contribution Frequency</span>
              <span className="detail-value">{club?.contributionFrequency || 'Weekly'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Contribution Amount</span>
              <span className="detail-value">${club?.contributionAmount || 0}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Bet Frequency</span>
              <span className="detail-value" style={{ textTransform: 'capitalize' }}>{club?.betFrequency || 'Weekly'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Bet Amount</span>
              <span className="detail-value">
                {club?.betAmountType === 'percent'
                  ? `${club?.betPercentage}% of kitty`
                  : club?.betPercentage
                    ? `$${club?.betPercentage}`
                    : 'Unknown'}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Next Betting Period</span>
              <span className="detail-value">{formatDate(puntingSchedule?.periodEnd)}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Captain</span>
              <span className="detail-value">{club?.captain?.name || 'Unknown'}</span>
            </div>
          </div>
        </section>

        {/* Members Section */}
        <section className="statement-section">
          <h2><i className="fas fa-users"></i> Members</h2>
          <p className="section-description">
            You can click on a member for more information and click on the headings to sort
          </p>

          <div className="members-table-container">
            <table className="members-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('name')} className="sortable">
                    Name
                    {sortColumn === 'name' && (
                      <i className={`fas fa-sort-${sortDirection === 'asc' ? 'up' : 'down'}`}></i>
                    )}
                  </th>
                  <th onClick={() => handleSort('contributions')} className="sortable">
                    Contributions
                    {sortColumn === 'contributions' && (
                      <i className={`fas fa-sort-${sortDirection === 'asc' ? 'up' : 'down'}`}></i>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedMembers.map((member) => (
                  <tr key={member.userId} className="member-row">
                    <td>
                      <div className="member-cell">
                        <span className="contribution-badge">{formatCurrency(member.contributions || 0)}</span>
                        <span className="member-name">{member.name}</span>
                      </div>
                    </td>
                    <td className="contribution-value">{formatCurrency(member.contributions || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="table-actions">
            <button className="btn-download">
              <i className="fas fa-download"></i> Download
            </button>
          </div>

          <div className="recalculate-note">
            <span>Contributions look incorrect? </span>
            <button className="link-btn">recalculate now</button>
          </div>
        </section>

        {/* Withdrawals Section */}
        <section className="statement-section">
          <h2><i className="fas fa-money-bill-wave"></i> Withdrawals</h2>
          <p className="no-data">No withdrawals made.</p>
        </section>

        {/* Transactions Section */}
        <section className="statement-section">
          <h2><i className="fas fa-right-left"></i> Transactions</h2>

          <div className="transaction-tabs">
            <button
              className={`tab-btn ${activeTab === 'kitty' ? 'active' : ''}`}
              onClick={() => setActiveTab('kitty')}
            >
              Kitty Account
            </button>
            <button
              className={`tab-btn ${activeTab === 'betting' ? 'active' : ''}`}
              onClick={() => setActiveTab('betting')}
            >
              Betting Account
            </button>
          </div>

          <div className="transactions-content">
            {activeTab === 'kitty' && (
              <div className="transactions-list">
                <p className="no-data">No kitty transactions to display.</p>
              </div>
            )}
            {activeTab === 'betting' && (
              <div className="transactions-list">
                <p className="no-data">No betting transactions to display.</p>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

export default ClubStatement;
